import Stack from "@mui/material/Stack/Stack";
import React, { useState } from "react";
import Paper from "@mui/material/Paper";
import {
  Button,
  TextField,
  Backdrop,
  CircularProgress,
  Typography,
  Fab,
  Input,
} from "@mui/material";
import QrCodeIcon from "@mui/icons-material/QrCode";
import { Container } from "@mui/system";
import DrawerAppBar from "../../../../../components/mobile/Navbar";
import { useNavigate } from "react-router-dom";
import { LoadProductList } from "../../../../../../domain/usecases/load-product-list";
import { Product } from "../../../../../../domain/models/product-model";
import { CustomDialog } from "../../../../../components/Dialog";
import RemoveIcon from "@mui/icons-material/Remove";
import AddIcon from "@mui/icons-material/Add";
import { useLocation } from "react-router-dom";
import { ValidateTransfer } from "../../../../../../domain/usecases/validate-transfer";

type Props = {
  loadProductList: LoadProductList;
  validateTransfer: ValidateTransfer;
};

export const TransferProductCollector: React.FC<Props> = ({
  loadProductList,
  validateTransfer,
}) => {
  const [code, setCode] = useState<string>("");
  const [batch, setBatch] = useState<string>("");
  const [amount, setAmount] = useState<string>("");
  const [product, setProduct] = useState<Product | null>(null);
  const [isLoading, setIsLoading] = useState<boolean>(false);
  const [isErrorDialogOpen, setIsErrorDialogOpen] = useState<boolean>(false);

  const navigate = useNavigate();
  const location = useLocation();

  const handleClick = async () => {
    if (!code) return alert("Preencha o código antes de continuar");
    if (product?.batchControl && !batch)
      return alert("Preencha o lote antes de continuar");

    const validation = await validateTransfer.validate({
      positionId: location.state.position.id!,
      productCode: code,
      batch,
      amount: Number(
        (Number(amount) * (product?.packageAmount || 1)).toFixed(2)
      ),
    });

    if (!validation.valid) {
      if (validation.error === "InventoryNotFound")
        return alert("Produto não encontrado nessa posição");
      if (validation.error === "BatchNotFound")
        return alert("O lote informado não consta na posição");
      if (validation.error === "InvalidAmount")
        return alert("Quantidade inválida");
      if (validation.error === "ReservedProduct")
        return alert("Este produto está reservado para separação");
      if (validation.error === "TransferAlreadyInProgress") {
        alert("Há uma transferência em andamento");
        return navigate("/mobile/tasks");
      }
    }
    if (validation.valid)
      return navigate("/mobile/transfer/finish", {
        state: { amount, code, batch },
      });
  };

  const handleInputBlur = async () => {
    setIsLoading(true);
    try {
      if (code) {
        if (product && product.eanCode === code) return;
        const data = await loadProductList.load({ code });
        setProduct(data[0]);
      }
    } catch (error) {
      setIsErrorDialogOpen(true);
    } finally {
      setIsLoading(false);
    }
  };

  return (
    <>
      <CustomDialog
        isOpen={isErrorDialogOpen}
        title="Erro ao carregar produto"
        content={`Não foi encontrado nenhum produto com o código EAN ${code}`}
        handleClose={() => {
          setIsErrorDialogOpen(false);
        }}
      />
      <Backdrop
        sx={{ color: "#fff", zIndex: (theme) => theme.zIndex.drawer + 1 }}
        open={isLoading}
      >
        <CircularProgress color="inherit" />
      </Backdrop>
      <DrawerAppBar
        title="Coletar dados do produto"
        onBack={() => {
          navigate("/mobile/transfer/position-collector");
        }}
      />
      <Stack sx={{ m: "20px" }} spacing={4}>
        <Paper>
          <Stack spacing={4} sx={{ mx: "10px", my: "30px" }}>
            <Stack direction="row">
              <TextField
                id="outlined-basic"
                label="Código do Produto"
                variant="outlined"
                sx={{
                  mx: "5px",
                }}
                value={code}
                onChange={(e) => {
                  setCode(e.target.value);
                }}
                onBlur={handleInputBlur}
              />
              <Button
                variant="outlined"
                color="inherit"
                sx={{
                  mx: "5px",
                }}
              >
                <QrCodeIcon />
              </Button>
            </Stack>
            {product?.batchControl ? (
              <Container>
                <TextField
                  id="outlined-basic"
                  label="Lote"
                  variant="outlined"
                  sx={{
                    mx: "5px",
                  }}
                  value={batch}
                  onChange={(e) => {
                    setBatch(e.target.value);
                  }}
                />
              </Container>
            ) : (
              ""
            )}
            <Stack>
              <Typography sx={{ mx: "5px" }}>Quantidade</Typography>
              <Stack
                direction="row"
                sx={{
                  my: "20px",
                  display: "flex",
                  alignItems: "center",
                  justifyContent: "center",
                }}
              >
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={() => {
                    if (amount && amount !== "0") {
                      const newValue = parseFloat(amount) - 1;
                      setAmount(newValue.toString());
                    }
                  }}
                >
                  <RemoveIcon />
                </Fab>

                <Input
                  sx={{ width: "70px", mx: "30px" }}
                  type="text"
                  value={amount}
                  onChange={(event) => {
                    const inputValue = event.target.value;
                    if (/^[0-9.]+$/.test(inputValue) || inputValue === "") {
                      setAmount(inputValue);
                    }
                  }}
                />
                <Fab
                  color="primary"
                  aria-label="add"
                  size="small"
                  onClick={() => {
                    if (amount) {
                      const newValue = parseFloat(amount) + 1;
                      setAmount(newValue.toString());
                    } else {
                      setAmount("1");
                    }
                  }}
                >
                  <AddIcon />
                </Fab>
              </Stack>
            </Stack>
          </Stack>
        </Paper>
        {product && (
          <Paper sx={{ padding: "20px" }}>
            <Stack spacing={2}>
              <Typography fontWeight="bold" fontSize="24px">
                {product.description}
              </Typography>
              <Typography>{`Fornecedor: ${product.supplier}`}</Typography>
              {batch && <Typography>{`Lote: ${batch}`}</Typography>}
              <Typography>{`Quantidade: ${amount}`}</Typography>
            </Stack>
          </Paper>
        )}

        <Button
          variant="contained"
          color="secondary"
          sx={{ color: "white", height: "50px", fontWeight: "bold" }}
          onClick={handleClick}
        >
          Continuar
        </Button>
      </Stack>
    </>
  );
};
